import i18n from 'i18n';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';

import { useAppDispatch } from 'store/store';
import { upgradeSubscription } from 'store/user-service/asyncActions';

import { NamespacesList } from 'core/enums/common';

import { InfoModal } from 'components/shared/Modal/Info';

import * as Styled from './styled';

interface TariffUpgradeConfirmationModalProps {
	open: boolean;
	handleClose: () => void;
	choosedPlan: { priceId: string; previewPrice: number } | null;
	handleGetActiveTariff: () => void;
}

export const TariffUpgradeConfirmationModal: FC<TariffUpgradeConfirmationModalProps> = ({
	open,
	handleClose,
	choosedPlan,
	handleGetActiveTariff,
}) => {
	const { t: modalTranslate } = useTranslation(NamespacesList['modals']);

	const [isLoading, setIsLoading] = useState(false);

	const dispatch = useAppDispatch();

	const handleConfirm = async () => {
		setIsLoading(true);
		try {
			await dispatch(upgradeSubscription({ newPriceId: choosedPlan?.priceId || '' })).unwrap();
			handleGetActiveTariff();
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoading(false);
			handleClose();
		}
	};

	const getTitle = useMemo(() => {
		if (i18n.language === 'ar') {
			return `$${choosedPlan?.previewPrice} ${modalTranslate('confirmTariffUpgrade.title')}`;
		}
		return `${modalTranslate('confirmTariffUpgrade.title')} $${choosedPlan?.previewPrice}`;
	}, [i18n.language, choosedPlan]);

	return (
		<InfoModal
			isOpen={open}
			image={<Styled.WarningIcon />}
			onClose={handleClose}
			title={getTitle}
			description={modalTranslate('confirmTariffUpgrade.description')}
			buttons={
				<>
					<Styled.CancelButton variant="outlined" onClick={handleClose}>
						{modalTranslate('confirmTariffUpgrade.cancel')}
					</Styled.CancelButton>

					{isLoading ? (
						<Styled.ConfirmButton variant="contained" disabled={isLoading}>
							<Oval
								height={24}
								width={24}
								color="rgba(0, 0, 0, 0.12)"
								wrapperStyle={{}}
								wrapperClass=""
								visible={true}
								ariaLabel="oval-loading"
								secondaryColor="rgba(0, 0, 0, 0.12)"
								strokeWidth={5}
								strokeWidthSecondary={5}
							/>
						</Styled.ConfirmButton>
					) : (
						<Styled.ConfirmButton onClick={handleConfirm}>
							{modalTranslate('confirmTariffUpgrade.confirm')}
						</Styled.ConfirmButton>
					)}
				</>
			}
		></InfoModal>
	);
};
